import { useField } from 'formik';
import { parseInt } from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, BoxProps, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { CashIcon, DeleteIcon, RulerIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Card,
	DraggableCard,
	FlexGap,
	Heading3,
	IconButton,
	NumericInput,
	Paragraph,
	PencilEditBoxIcon,
	ReorderRenderState,
	Skeleton,
	Todo,
	tokens,
} from '@treams/treams-ui';

import { GoodHabitzLogo } from '../GoodHabitzLogo';
import EditSubgoalCard from './EditSubgoalCard';

const Strikethrough = styled.span<{ late?: boolean }>`
	text-decoration: line-through;
	color: ${(props) => (props.late ? tokens.warning.text : tokens.text.secondary)};
`;

const SubgoalIcon = styled(Box)<{ completed: boolean }>`
	color: ${(props) => (props.completed ? tokens.success.icon : tokens.icon.secondary)};

	svg {
		display: block;
		width: 24px;
		height: 24px;
		color: inherit;

		path {
			fill: currentColor;
		}
	}
`;

const TitleBlock = styled(Box)`
	flex: 1 1 0%;
	min-width: 0;
	overflow-wrap: anywhere;
`;

export default Skeleton(
	function SubgoalCard(props: {
		subgoalID: string;
		editable?: boolean;
		goalCompleted?: boolean;
		dragHandleProps?: ReorderRenderState['dragHandleProps'];
		isDragging?: boolean;
	}) {
		const { t } = useTranslation();
		const { localize } = useLocalize();

		const [{ value }, , { setValue }] = useField<graphql.GoalChecklistItem>(
			`subgoals.${props.subgoalID}`,
		);

		const [{ value: subgoalIDs }, , { setValue: setSubgoalIDs }] = useField<string[]>('subgoalIDs');

		const isDue =
			moment(value.dueDate).isBefore(moment()) && value.status === graphql.ChecklistItemStatus.Todo;

		const [edit, setEdit] = useState(false);
		const [progressValue, setProgressValue] = useState<number | undefined>(value.progress.value);

		const learningType = localize(value.learningCourse?.learningType.title)?.text?.toLowerCase();
		const launchUrl =
			localize(value.learningCourse?.launchUrls)?.text ?? value.learningCourse?.launchUrl;

		if (edit) {
			return (
				<EditSubgoalCard
					subgoalID={props.subgoalID}
					dragHandleProps={props.dragHandleProps}
					isDragging={props.isDragging}
					onDismiss={() => setEdit(false)}
				/>
			);
		}

		const hasNumericProgress =
			value.progress.type === graphql.GoalChecklistItemType.Currency ||
			value.progress.type === graphql.GoalChecklistItemType.Range ||
			value.progress.type === graphql.GoalChecklistItemType.Percentage;
		const hasLearningAction =
			value.progress.type === graphql.GoalChecklistItemType.Learning && Boolean(launchUrl);
		const showTrailing = hasNumericProgress || hasLearningAction || Boolean(props.editable);

		return (
			<DraggableCard
				dragHandleProps={props.dragHandleProps ?? undefined}
				isDragging={props.isDragging}
				isDragDisabled={!props.dragHandleProps}
			>
				<FlexGap
					alignItems={['flex-start', 'flex-start', 'center']}
					flexGrow={1}
					flexWrap={['wrap', 'wrap', 'nowrap']}
					gap={tokens.gap.list}
					minWidth={0}
					width="100%"
				>
					{(value.progress.type === graphql.GoalChecklistItemType.Checkbox ||
						value.progress.type === graphql.GoalChecklistItemType.Learning) && (
						<Todo
							checked={value.progress.value === 1}
							late={value.progress.value !== 1 && props.goalCompleted}
							mr={`${tokens.gap.element}px`}
							flexGrow={0}
							flexShrink={0}
							onChange={
								props.editable
									? (checked) =>
											setValue({
												...value,
												progress: {
													...value.progress,
													value: checked ? 1 : 0,
												},
												completedAt: checked ? new Date() : undefined,
											})
									: undefined
							}
						/>
					)}

					{value.progress.type === graphql.GoalChecklistItemType.Currency && (
						<SubgoalIcon width={24} mr={`${tokens.gap.element}px`} completed={!!value.completedAt}>
							<CashIcon />
						</SubgoalIcon>
					)}

					{value.progress.type === graphql.GoalChecklistItemType.Range && (
						<SubgoalIcon width={24} mr={`${tokens.gap.element}px`} completed={!!value.completedAt}>
							<RulerIcon />
						</SubgoalIcon>
					)}

					<TitleBlock>
						<Heading3>
							{value.completedAt ? (
								<Strikethrough>{localize(value.title)?.text}</Strikethrough>
							) : props.goalCompleted ? (
								<Strikethrough late>{localize(value.title)?.text}</Strikethrough>
							) : (
								localize(value.title)?.text
							)}
						</Heading3>
						{(value.dueDate || value.completedAt || value.createdAt) && (
							<Paragraph mt="4px">
								{value.dueDate && (
									<span style={{ color: isDue ? tokens.error.text : undefined }}>
										{t('subgoals.card.dueDate', { dueDate: moment(value.dueDate).format('LL') })}
									</span>
								)}

								{value.dueDate && ' · '}

								{value.completedAt
									? t('subgoals.card.completedAt', {
											completedAt: moment(value.completedAt).format('LL'),
										})
									: value.createdAt &&
										t('subgoals.card.createdAt', {
											date: moment(value.createdAt).format('LL'),
										})}
							</Paragraph>
						)}
					</TitleBlock>

					{showTrailing && (
						<FlexGap
							alignItems="center"
							flex={['1 1 100%', '1 1 100%', '0 0 auto']}
							flexWrap={['wrap', 'wrap', 'nowrap']}
							gap={tokens.gap.list}
							justifyContent={['flex-end', 'flex-end', 'flex-start']}
						>
							{hasNumericProgress && (
								<Flex alignItems="center">
									<Box
										flex={['1 1 auto', '1 1 auto', '0 0 auto']}
										minWidth={0}
										mr={3}
										width={['auto', 'auto', 140]}
									>
										<Flex justifyContent="flex-end">
											{props.editable ? (
												<NumericInput
													prefix={
														value.progress.type === graphql.GoalChecklistItemType.Currency
															? '€'
															: undefined
													}
													value={String(progressValue)}
													onChange={(newValue) => setProgressValue(parseInt(newValue))}
												/>
											) : (
												(progressValue ?? 0).toString()
											)}
										</Flex>
									</Box>
									<Flex alignItems="center">
										<Box mr={2}>
											<Paragraph>/</Paragraph>
										</Box>

										{value.progress.type === graphql.GoalChecklistItemType.Currency && (
											<Box minWidth={8}>&euro;</Box>
										)}
										{value.progress.upper && value.progress.upper.toLocaleString('nl-NL')}
									</Flex>
								</Flex>
							)}

							{value.progress.type === graphql.GoalChecklistItemType.Learning && launchUrl && (
								<Button
									type="secondary"
									small
									icon={<GoodHabitzLogo />}
									title={
										learningType
											? t('goals.subgoals.viewLearningType.button', {
													learningType,
												})
											: t('goals.subgoals.viewItem.button')
									}
									link={launchUrl}
									target="_blank"
									flexGrow={0}
									flexShrink={0}
								/>
							)}

							{props.editable &&
								(progressValue !== undefined &&
								value.progress.upper !== undefined &&
								progressValue !== value.progress.value &&
								value.progress.type !== graphql.GoalChecklistItemType.Checkbox &&
								value.progress.type !== graphql.GoalChecklistItemType.Learning ? (
									<Button
										title={t('goals.subgoals.save.button')}
										small
										onClick={() =>
											setValue({
												...value,
												progress: { ...value.progress, value: progressValue },
												completedAt:
													progressValue >= value.progress.upper! ? new Date() : undefined,
											})
										}
										width={72}
									/>
								) : (
									<>
										<IconButton
											type="secondary"
											onClick={() => setEdit(true)}
											icon={<PencilEditBoxIcon />}
											flexGrow={0}
											flexShrink={0}
										/>
										<IconButton
											type="destructive"
											onClick={() => {
												setSubgoalIDs(subgoalIDs.filter((id) => id !== props.subgoalID));
											}}
											icon={<DeleteIcon />}
											flexGrow={0}
											flexShrink={0}
										/>
									</>
								))}
						</FlexGap>
					)}
				</FlexGap>
			</DraggableCard>
		);
	},
	function SubgoalCardSkeleton(props: BoxProps) {
		return (
			<Card.Skeleton p={`${tokens.padding.container}px`} {...props}>
				<Heading3.Skeleton mb={1} />
				<Paragraph.Skeleton lines={1} />
			</Card.Skeleton>
		);
	},
);
